
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

  const REGION_TO_GOVERNMENTS = {
  Egypt: [
    'Cairo','Giza','Alexandria','Dakahlia','Sharqia','Qalyubia','Kafr El Sheikh','Gharbia','Monufia','Beheira','Ismailia','Suez','Beni Suef','Faiyum','Minya','Asyut','Sohag','Qena','Aswan','Luxor','Red Sea','Matrouh','North Sinai','South Sinai','New Valley'
  ],
  'Saudi Arabia': [
    'Riyadh','Makkah','Madinah','Qassim','Eastern Province','Asir','Tabuk','Hail','Northern Borders','Jazan','Najran','Al Bahah','Al Jawf'
  ],
  Syria: [
    'Damascus','Rif Dimashq','Aleppo','Hama','Homs','Latakia','Tartus','Idlib','Deir ez-Zor','Raqqa','Hasakah','Daraa','Quneitra','As-Suwayda'
  ],
  'United Arab Emirates': ['Abu Dhabi','Dubai','Sharjah','Ajman','Umm Al Quwain','Ras Al Khaimah','Fujairah'],
  Jordan: ['Amman','Balqa','Zarqa','Irbid','Mafraq','Jarash','Ajloun','Karak','Tafilah','Maʼan','Aqaba'],
  Lebanon: ['Beirut','Mount Lebanon','North','South','Nabatieh','Beqaa','Baalbek-Hermel','Akkar'],
  Morocco: ['Casablanca-Settat','Rabat-Salé-Kénitra','Marrakesh-Safi','Fès-Meknès','Tanger-Tétouan-Al Hoceïma']
};

export default function OrderSummary() {
  const navigate = useNavigate();
  const { cart, updateShipping, updatePayment, getSubtotal, finalizeOrder, clearCart } = useCart();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;
  const key = (base) => (uid ? `${base}:${uid}` : null);

  const [contact, setContact] = useState({ email: '', phone: '' });
  const [region, setRegion] = useState('Egypt');
  const governments = useMemo(() => REGION_TO_GOVERNMENTS[region] || [], [region]);
  const [shipping, setShipping] = useState({ firstName:'', lastName:'', address1:'', address2:'', city:'', government:'', postalCode:'', apartmentType:'Apartment', phone:'' });
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [billing, setBilling] = useState({ firstName:'', lastName:'', address1:'', address2:'', city:'', government:'', postalCode:'' });
  const [saveContact, setSaveContact] = useState(true);
  const [saveShippingInfo, setSaveShippingInfo] = useState(true);

  useEffect(() => {
    if (!cart?.items || cart.items.length === 0 || (cart.status && cart.status !== 'cart')) {
      navigate('/cart');
    }
  }, [cart, navigate]);

  // One-time migration and per-user load
  useEffect(() => {
    // wipe legacy global keys once
    localStorage.removeItem('contactInfo');
    localStorage.removeItem('shippingInfo');
    localStorage.removeItem('billingInfo');
    if (!uid) return; // wait for auth
    try {
      const c = JSON.parse(localStorage.getItem(`contactInfo:${uid}`) || 'null');
      if (c) setContact({ email: c.email || '', phone: c.phone || '' });
      const s = JSON.parse(localStorage.getItem(`shippingInfo:${uid}`) || 'null');
      if (s) {
        setRegion(s.region || 'Egypt');
        setShipping({
          firstName: s.firstName || '', lastName: s.lastName || '',
          address1: s.address1 || '', address2: s.address2 || '',
          city: s.city || '', government: s.government || '', postalCode: s.postalCode || '',
          apartmentType: s.apartmentType || 'Apartment',
          phone: s.phone || ''
        });
      }
    } catch {}
  }, [uid]);

  const total = getSubtotal();

  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!contact.email) e.email = 'Email is required';
    if (!contact.phone) e.phone = 'Phone is required';
    if (!region) e.region = 'Region is required';
    if (!shipping.government) e.government = 'Government is required';
    if (!shipping.firstName) e.firstName = 'First name is required';
    if (!shipping.lastName) e.lastName = 'Last name is required';
    if (!shipping.address1) e.address1 = 'Address is required';
    if (!shipping.city) e.city = 'City is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const shippingDetails = {
      contact,
      region,
      ...shipping,
      billing: billingSameAsShipping ? { same: true } : { same: false, ...billing },
      method: 'standard'
    };
    
    try {
      // Update shipping and payment info
      await updateShipping(shippingDetails);
      await updatePayment({ method: paymentMethod });
      
      // Save user preferences
      if (uid) {
        if (saveContact) localStorage.setItem(`contactInfo:${uid}`, JSON.stringify(contact));
        if (saveShippingInfo) localStorage.setItem(`shippingInfo:${uid}`, JSON.stringify({ region, ...shipping }));
        if (!billingSameAsShipping && saveShippingInfo) localStorage.setItem(`billingInfo:${uid}`, JSON.stringify(billing));
      }
      
      // Finalize order with shipping details
      await finalizeOrder(shippingDetails);
      clearCart();
      navigate('/order-complete');
    } catch (err) {
      console.error('Finalize order failed', err);
      alert('Failed to complete order. Please try again.');
    }
  };

  const items = cart?.items || [];

  return (
    <div className="page-container">
      <header className="page-header">
        <h1>Order Summary</h1>
        <p>Review your items and enter delivery details</p>
      </header>

      <main className="page-content" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>
        <form onSubmit={handleSubmit} className="checkout-form">
          <div className="form-section">
            <h3>1. Contact</h3>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={contact.email} onChange={(e)=>setContact({ ...contact, email: e.target.value })} className={errors.email ? 'input-error' : ''} />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input type="tel" value={contact.phone} onChange={(e)=>setContact({ ...contact, phone: e.target.value })} className={errors.phone ? 'input-error' : ''} />
              {errors.phone && <span className="error-text">{errors.phone}</span>}
            </div>
            <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
              <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                <input type="checkbox" checked={saveContact} onChange={(e)=>setSaveContact(e.target.checked)} /> Save contact for next time
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                <input type="checkbox" checked={saveShippingInfo} onChange={(e)=>setSaveShippingInfo(e.target.checked)} /> Save shipping for next time
              </label>
            </div>
          </div>

          <div className="form-section">
            <h3>2. Shipping address</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Region</label>
                <select value={region} onChange={(e)=>{ setRegion(e.target.value); setShipping((s)=>({ ...s, government: '' })); }} className={errors.region ? 'input-error' : ''}>
                  {Object.keys(REGION_TO_GOVERNMENTS).map((r)=> <option key={r} value={r}>{r}</option>)}
                </select>
                {errors.region && <span className="error-text">{errors.region}</span>}
              </div>
              <div className="form-group">
                <label>Government</label>
                <select value={shipping.government} onChange={(e)=>setShipping({ ...shipping, government: e.target.value })} className={errors.government ? 'input-error' : ''}>
                  <option value="" disabled>Choose...</option>
                  {governments.map((g)=> <option key={g} value={g}>{g}</option>)}
                </select>
                {errors.government && <span className="error-text">{errors.government}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>First name</label>
                <input value={shipping.firstName} onChange={(e)=>setShipping({ ...shipping, firstName: e.target.value })} className={errors.firstName ? 'input-error' : ''} />
                {errors.firstName && <span className="error-text">{errors.firstName}</span>}
              </div>
              <div className="form-group">
                <label>Last name</label>
                <input value={shipping.lastName} onChange={(e)=>setShipping({ ...shipping, lastName: e.target.value })} className={errors.lastName ? 'input-error' : ''} />
                {errors.lastName && <span className="error-text">{errors.lastName}</span>}
              </div>
            </div>
            <div className="form-group">
              <label>Address</label>
              <input value={shipping.address1} onChange={(e)=>setShipping({ ...shipping, address1: e.target.value })} className={errors.address1 ? 'input-error' : ''} />
              {errors.address1 && <span className="error-text">{errors.address1}</span>}
            </div>
            <div className="form-group">
              <label>Apartment, villa, suite (optional)</label>
              <input value={shipping.address2} onChange={(e)=>setShipping({ ...shipping, address2: e.target.value })} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input value={shipping.city} onChange={(e)=>setShipping({ ...shipping, city: e.target.value })} className={errors.city ? 'input-error' : ''} />
                {errors.city && <span className="error-text">{errors.city}</span>}
              </div>
              <div className="form-group">
                <label>Postal code</label>
                <input value={shipping.postalCode} onChange={(e)=>setShipping({ ...shipping, postalCode: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>3. Payment</h3>
            <div className="form-group">
              <label className="radio-option">
                <input type="radio" name="pay" value="cod" checked={paymentMethod==='cod'} onChange={()=>setPaymentMethod('cod')} />
                Cash on Delivery (COD)
              </label>
              <label className="radio-option">
                <input type="radio" name="pay" value="card" checked={paymentMethod==='card'} onChange={()=>setPaymentMethod('card')} />
                Credit / Debit Card (coming soon)
              </label>
            </div>
          </div>

          <div className="form-section">
            <h3>4. Billing address</h3>
            <label className="radio-option">
              <input type="radio" name="bill" checked={billingSameAsShipping} onChange={()=>setBillingSameAsShipping(true)} /> Same as delivery address
            </label>
            <label className="radio-option">
              <input type="radio" name="bill" checked={!billingSameAsShipping} onChange={()=>setBillingSameAsShipping(false)} /> Use a different billing address
            </label>
            {!billingSameAsShipping && (
              <div className="form-section" style={{ marginTop: '1rem' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>First name</label>
                    <input value={billing.firstName} onChange={(e)=>setBilling({ ...billing, firstName: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Last name</label>
                    <input value={billing.lastName} onChange={(e)=>setBilling({ ...billing, lastName: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input value={billing.address1} onChange={(e)=>setBilling({ ...billing, address1: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Apartment, villa, suite (optional)</label>
                  <input value={billing.address2} onChange={(e)=>setBilling({ ...billing, address2: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input value={billing.city} onChange={(e)=>setBilling({ ...billing, city: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Postal code</label>
                    <input value={billing.postalCode} onChange={(e)=>setBilling({ ...billing, postalCode: e.target.value })} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-button">Complete Order</button>
          </div>
        </form>

        <aside>
          <div className="form-section">
            <h3>Items</h3>
            {items.length === 0 ? (
              <p>No items in cart.</p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display:'flex', flexDirection:'column', gap: '0.75rem' }}>
                {items.map((it, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 56, height: 56, background:'#f1f3f5', borderRadius:8, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {it.photos?.[0]?.url ? (
                        <img src={it.photos[0].url} alt={it.title} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                      ) : (
                        <span style={{ fontSize:12, color:'#6c757d' }}>No photo</span>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{it.title}</div>
                      <div style={{ color:'#6c757d', fontSize: 12 }}>Qty: {it.qty || 1}</div>
                    </div>
                    <div style={{ fontWeight: 600 }}>${((it.price || 0) * (it.qty || 1)).toFixed(2)}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="form-section">
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span>Subtotal</span>
              <strong>${total.toFixed(2)}</strong>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span>Shipping</span>
              <span>Calculated at next step</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', borderTop:'1px solid #e9ecef', paddingTop:8 }}>
              <span>Total</span>
              <strong>${total.toFixed(2)}</strong>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}


